/**
 * MenuManagementContainer
 * --
 */
import React, { useEffect, useState } from 'react';
import MenuManagementPresenter from './MenuManagementPresenter';
import {
  useCreateMenu,
  useGetMenus,
  useUpdateMenu,
  menuQueryKeys,
} from 'hooks/MenuHooks';
import {
  useCreateShopMenu,
  useGetShopMenus,
  useUpdateShopMenu,
  shopMenuQueryKeys,
} from 'hooks/ShopMenuHooks';
import {
  useCreateProductCategory,
  useGetMenuCategories,
  useUpdateProductCategory,
  productCategoryQueryKeys,
} from 'hooks/ProductCategoryHooks';
import { useCreateMenuFile } from 'hooks/MenuFileHooks';
import { useFileUpload } from 'hooks/FileHooks';
import { LoadingPage } from 'components';
import MessageAlert from 'utils';
import { useUserStack } from 'store';
import { useQueryClient } from '@tanstack/react-query';

const MenuManagementContainer = () => {
  /* ===== ROUTE ===== */
  const queryClient = useQueryClient();
  /* ===== PROPS ===== */
  /* ===== Mutate ===== */
  const userInfo = useUserStack();
  const { createMenu } = useCreateMenu();
  const { updateMenu } = useUpdateMenu();
  const { createMenuFile } = useCreateMenuFile();
  const { fileUpload } = useFileUpload();
  const { createProductCategory } = useCreateProductCategory();
  const { updateProductCategory } = useUpdateProductCategory();
  const { createShopMenu } = useCreateShopMenu();
  const { updateShopMenu } = useUpdateShopMenu();

  /* ===== Data ===== */
  // 메뉴 전체 조회
  const { data: Menus } = useGetMenus({
    join_tables: '["menu_file"]',
  });
  // 메뉴 카테고리 전체 조회
  const { data: Categories } = useGetMenuCategories();

  // 메뉴 카테고리 전체 조회
  const { data: ShopMenus } = useGetShopMenus();
  // 데이터 리패치
  const getQueryKey = {
    MENU: () =>
      menuQueryKeys.getMenus({
        join_tables: '["menu_file"]',
      }),
    CATEGORY: () => productCategoryQueryKeys.getMenuCategories(),
    SHOPMENU: () => shopMenuQueryKeys.getShopMenus(),
    SHOPMENUEDIT: () => shopMenuQueryKeys.getShopMenus(),
  };
  /* ===== STATE ===== */
  const [data, setData] = useState([]);
  const [menuData, setMenuData] = useState([]);
  const [shopMenuData, setShopMenuData] = useState([]);
  const [privateShopMenu, setPrivateShopMenu] = useState([]);

  const [selectedItem, setSelectedItem] = useState({});
  // 모달 open 여부
  const [isOpened, setIsOpened] = useState(false);
  // 모달 타입
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = React.useState({
    menu: {},
    category: {},
  });
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);

  // 메뉴 파일
  const [files, setFiles] = useState([]);
  const [thumbnailFiles, setThumbnailFiles] = React.useState([]);

  // 메뉴 옵션
  const [options, setOptions] = useState([]);
  // 영양 정보 옵션
  const [nutritionalInfo, setNutritionalInfo] = React.useState([]);
  // 순서 편집 데이터
  const [orderList, setOrderList] = useState([]);
  const [isRefetch, setIsRefetch] = React.useState(false);
  //메뉴 선택 데이터
  const [selectedMenus, setSelectedMenus] = useState([]);

  //메뉴 수량, 대면 판매여부 편집
  const [isEditMode, setIsEditMode] = useState(false);

  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */
  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '', data = {}) => {
    setModalData((prev) => ({
      ...prev,
      [type]: data,
    }));
    if (data?.menu_file?.length > 0) {
      const thumbnailFiles = data.menu_file.filter(
        (file) => file.type === 'THUMBNAIL'
      );
      const otherFiles = data.menu_file.filter(
        (file) => file.type !== 'THUMBNAIL'
      );

      setThumbnailFiles(thumbnailFiles);
      setFiles(otherFiles);
    } else {
      setThumbnailFiles([]);
      setFiles([]);
    }
    setOptions(data?.menu_option || []);
    setNutritionalInfo(data?.nutritional_information || []);
    setIsOpened(!open);
    setModalType(type);
  };

  /**
   * 편집 핸들러
   * --
   */
  const handleEdit = (edit, type = '', data = {}) => {
    setModalData((prev) => ({
      ...prev,
      [type]: data,
    }));
    setIsEditMode(edit);
  };

  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, o) => {
    let newData = modalData[o];

    newData[t] = v;
    const d = {
      ...modalData,
      o: { ...newData },
    };

    setModalData(d);
  };
  /**
   * 메뉴 옵션 바인딩 함수
   * --
   */
  const handleOption = (data, index) => {
    let newData = [...options];

    newData[index] = data;
    setOptions(newData);
  };

  /**
   * 영양 정보 상태 업데이트 함수
   * --
   * @param {string} action - 수행할 작업 ('add', 'update', 'remove')
   * @param {object} [data] - 업데이트할 데이터 객체 (예: { nutritional_title: '칼로리', nutritional_amount: '100', nutritional_type: 'kcal' })
   * @param {number} [index] - 업데이트 또는 삭제할 항목의 인덱스
   */
  const handleNutritionalInfo = (action, data = {}, index = null) => {
    setNutritionalInfo((prevInfo) => {
      let updatedInfo = [...prevInfo];

      if (action === 'add') {
        // 새 항목 추가
        updatedInfo.push({
          nutritional_info_id: prevInfo.length + 1, // 유일한 ID 할당
          nutritional_title: '',
          nutritional_type: 'mg',
          nutritional_amount: 0,
        });
      } else if (action === 'update' && index !== null) {
        // 특정 인덱스의 항목 업데이트
        updatedInfo[index] = { ...updatedInfo[index], ...data };
      } else if (action === 'remove' && index !== null) {
        // 특정 인덱스의 항목 삭제
        updatedInfo.splice(index, 1);
      }

      // 업데이트된 영양 정보를 상태에 반영
      return updatedInfo;
    });
  };

  const handleSubmit = async (t, s = 'CREATE') => {
    const type = t.toLocaleUpperCase();
    const submit = s.toLocaleUpperCase();

    const submitType = {
      MENU: '메뉴',
      SHOPMENU: '매장메뉴',
      SHOPMENUEDIT: '매장메뉴',
      CATEGORY: '카테고리',
      CREATE: '생성',
      UPDATE: '수정',
    };

    const data = handleValidateCheck(type);
    if (!data) return;

    const submitFunc = {
      MENU: {
        CREATE: createMenu,
        UPDATE: updateMenu,
      },
      SHOPMENU: {
        CREATE: createShopMenu,
        UPDATE: updateShopMenu,
      },
      SHOPMENUEDIT: {
        UPDATE: updateShopMenu,
      },
      CATEGORY: {
        CREATE: createProductCategory,
        UPDATE: updateProductCategory,
      },
    };

    try {
      setIsLoading(true);

      let allSuccess = true;

      if (type === 'SHOPMENUEDIT' && submit === 'UPDATE') {
        // `isEditMode`일 경우 수량과 판매 여부 업데이트 처리
        const updatedData = {
          shop_menu_id: modalData?.menu?.shop_menu_id,
          num: modalData?.menu?.num,
          is_offline: modalData?.menu?.is_offline,
        };

        const { status } = await updateShopMenu(updatedData);
        if (status !== 200) {
          allSuccess = false;
        }
      } else if (type === 'SHOPMENU' && submit === 'CREATE') {
        // 기존 데이터 매핑
        const existingMenuMap = new Map(
          privateShopMenu.map((menu) => [menu.menu_id, menu])
        );
        const selectedMenuIds = new Set(data?.selectedMenusIdx);

        // 업데이트: 기존 데이터에서 선택되지 않은 메뉴를 'is_show' = 'N'으로 업데이트
        for (const [menu_id, menu] of existingMenuMap.entries()) {
          if (!selectedMenuIds.has(menu_id)) {
            // selectedMenuIds에 없는 메뉴를 'N'으로 업데이트
            const updatedMenuData = {
              shop_menu_id: menu.shop_menu_id,
              is_show: 'N',
            };
            const { status } = await updateShopMenu(updatedMenuData);
            if (status !== 200) {
              allSuccess = false;
              break;
            }
          } else if (menu.is_show === 'N') {
            // selectedMenuIds에 있고, 기존 데이터에서 'is_show'가 'N'인 경우 'Y'로 업데이트
            const updatedMenuData = {
              shop_menu_id: menu.shop_menu_id,
              is_show: 'Y',
            };
            const { status } = await updateShopMenu(updatedMenuData);
            if (status !== 200) {
              allSuccess = false;
              break;
            }
          }
        }

        // 생성: selectedMenusIdx에 있지만 existingMenuMap에 없는 메뉴를 새로 생성
        for (const menu_id of data.selectedMenusIdx) {
          if (!existingMenuMap.has(menu_id)) {
            const menuData = {
              menu_id: menu_id,
              shop_information_id: userInfo?.shop_information_id,
              num: 100,
              menu_status: 'NORMAL',
              is_show: 'Y',
            };
            const { status } = await createShopMenu(menuData);
            if (status !== 200) {
              allSuccess = false;
              break;
            }
          }
        }
      } else {
        const { status, data: createdMenuData } = await submitFunc[type][
          submit
        ]({
          ...data,
        });

        if (status === 200) {
          // 파일 업로드 처리
          if (type === 'MENU' && files && files.length > 0) {
            // 파일 리스트 정제
            const Newfiles = [
              ...thumbnailFiles?.map((file) => {
                file.category = 'THUMBNAIL';
                return file;
              }),
              ...files,
            ];
            const menu_id = createdMenuData.menu_id; // 생성된 메뉴 ID
            const fileSavePromises = Newfiles.map(async (file, index) => {
              const timestamp = Date.now();
              const fileExtension = file?.name?.split('.').pop();
              const baseName = file?.name?.replace(`.${fileExtension}`, '');
              const newBaseName = baseName?.replace(/[^a-zA-Z0-9_.-]/g, '_');
              const fileName = `jm_coffee/menus/${menu_id}/${newBaseName}_${timestamp}.${fileExtension}`;

              // 각 파일을 개별적으로 업로드
              const uploadResponse = await fileUpload({
                file,
                file_name: fileName,
              });

              if (uploadResponse) {
                // 파일 정보 저장
                const menuFileInfo = {
                  menu_id,
                  file_name: `${newBaseName}_${timestamp}.${fileExtension}`,
                  file_url: fileName,
                  type: file.category,
                };
                return createMenuFile(menuFileInfo);
              }
            });

            // 모든 파일 저장이 완료될 때까지 대기
            await Promise.all(fileSavePromises);
          }
        }
        if (status !== 200) {
          allSuccess = false;
        }
      }

      if (allSuccess) {
        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`
        );
        setModalData((prev) => ({ ...prev, t: {} }));
        setIsOpened(false);
        setIsRefetch(true);
        const queryKey = getQueryKey[type]();
        queryClient.invalidateQueries({
          queryKey: queryKey.queryKey,
        });
        setSelectedItem((prev) => ({ ...prev, ...data?.modalData?.menu }));
      } else {
        MessageAlert.warning(
          `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`
        );
      }
    } catch (err) {
      MessageAlert.error(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`
      );
      console.error(`[CREATE ${type}] ERROR : `, err);
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 유효성 검사
   * --
   * @param {string} t
   * @returns
   */
  const handleValidateCheck = (t) => {
    if (t === 'MENU') {
      const select_menu_length =
        data?.filter(
          (i) =>
            i.product_category_id === modalData?.category?.product_category_id
        )?.[0]?.menus?.length + 1;

      // 메뉴 유효성 검사
      const {
        menu_name,
        price,
        menu_content,
        temperature_option,
        is_show,
        type,
        exchange_return_policy,
        is_present,
        present_information,
      } = modalData?.menu;
      // 옵션 데이터 처리: option_type이 선택되지 않았을 경우 COUNT로 설정
      const processedOptions = options.map((option) => ({
        ...option,
        type: 'DEFAULT',
        option_type: option.option_type || 'COUNT', // option_type이 없으면 COUNT로 설정
        option: option.option.map((subOption) => ({
          ...subOption,
          option_type: option.option_type || 'COUNT', // 하위 옵션도 마찬가지로 처리
        })),
      }));

      if (!menu_name) {
        MessageAlert.warning('메뉴명을 입력해주세요.');
        return false;
      }
      if (price === null || price === undefined || price === '') {
        MessageAlert.warning('단가를 입력해주세요.');
        return false;
      }
      if (!type) {
        MessageAlert.warning('메뉴타입을 선택해주세요.');
        return false;
      }
      if (!is_show) {
        MessageAlert.warning('노출여부를 선택해주세요.');
        return false;
      }
      if (!menu_content) {
        MessageAlert.warning('메뉴설명을 입력해주세요.');
        return false;
      }
      if (menu_content?.length > 200) {
        MessageAlert.warning('메뉴설명의 길이를 확인해주세요');
        return false;
      }
      if (exchange_return_policy?.length > 200) {
        MessageAlert.warning('교환/반품 안내의 길이를 확인해주세요');
        return false;
      }
      if (type === 'GENERAL') {
        if (!temperature_option) {
          MessageAlert.warning('온도옵션을 선택해주세요.');
          return false;
        }
      }
      if (is_present === 'Y') {
        if (present_information?.present_expired_date === '') {
          MessageAlert.warning('선물유효기간을 선택해주세요.');
          return false;
        }
        if (present_information?.present_postponed_deadline === '') {
          MessageAlert.warning('최대연장기간을 선택해주세요.');
          return false;
        }
      }
      return {
        ...modalData?.menu,
        shop_information_id: userInfo.shop_information_id,
        num: 100,
        temperature_option: type !== 'GENERAL' ? 'OTHER' : temperature_option,
        product_category_id: modalData?.menu?.product_category_id,
        application_status: 'APPLY',
        nutritional_information: nutritionalInfo,
        order: select_menu_length,
        menu_option: processedOptions,
        is_present: modalData?.menu?.is_present || 'N',
        present_information:
          modalData?.menu?.is_present === 'Y'
            ? {
                present_expired_date:
                  modalData?.menu?.present_information?.present_expired_date,
                present_postponed_deadline:
                  modalData?.menu?.present_information
                    ?.present_postponed_deadline,
              }
            : null,
      };
    } else if (t === 'CATEGORY') {
      // 카테고리 유효성 검사
      const { product_category_name } = modalData?.category;

      if (!product_category_name) {
        MessageAlert.warning('카테고리명을 입력해주세요.');
        return false;
      }

      return {
        ...modalData?.category,
        application_status: 'APPLY',
        type: 'MENU',
        shop_information_id: userInfo?.shop_information_id,
        register: 'STORE',
        order: Categories?.data?.length + 1,
      };
    } else if (t === 'SHOPMENU') {
      // 매장메뉴 유효성 검사
      if (
        !selectedMenus ||
        !Array.isArray(selectedMenus) ||
        selectedMenus.length === 0
      ) {
        MessageAlert.warning('선택된 메뉴가 없습니다.');
        return false;
      }

      const selectedMenusIdx = selectedMenus
        .map((item) => {
          if (!item.menu_id) {
            console.error('Invalid item without menu_id:', item);
            return null;
          }
          return item.menu_id;
        })
        .filter((id) => id !== null);

      if (selectedMenusIdx.length === 0) {
        MessageAlert.warning('유효한 메뉴 ID가 없습니다.');
        return false;
      }

      if (
        selectedMenusIdx.slice().sort().toString() ===
        shopMenuData.slice().sort().toString()
      ) {
        MessageAlert.warning('현재 저장되어 있는 메뉴와 같습니다.');
        return false;
      }
      return {
        selectedMenusIdx,
      };
    } else if (t === 'SHOPMENUEDIT') {
      return {
        modalData,
      };
    }
  };

  /**
   * 매장 메뉴 추가
   */

  // 메뉴 선택/해제 함수
  const handleSelect = (
    menu,
    category_name,
    parents_id,
    parents_category_name
  ) => {
    setSelectedMenus((prevSelectedMenus) => {
      const isSelected = prevSelectedMenus.some(
        (item) => item.menu_id === menu.menu_id
      );

      if (isSelected) {
        return prevSelectedMenus.filter(
          (item) => item.menu_id !== menu.menu_id
        );
      } else {
        const updatedMenu = {
          ...menu,
          category_name,
          parents_id,
          parents_category_name,
        };
        return [...prevSelectedMenus, updatedMenu];
      }
    });
  };

  // 선택된 메뉴를 카테고리별로 그룹화
  const groupedSelectedMenus = (selectedMenus || [])
    .sort((a, b) => a.product_category_order - b.product_category_order) // product_category_id 순서로 정렬
    .reduce((acc, menu) => {
      const parentCategoryName =
        menu?.parents_category_name || menu?.category_name || '기타'; // 상위 카테고리 이름
      const subCategoryName = menu?.parents_id ? menu?.category_name : null; // 하위 카테고리 이름

      // 상위 카테고리 생성
      if (!acc[parentCategoryName]) {
        acc[parentCategoryName] = {
          menus: [], // 상위 카테고리 메뉴 리스트
          subCategories: {}, // 하위 카테고리 그룹
        };
      }

      // 하위 카테고리가 존재하는 경우, 하위 카테고리에 메뉴 추가
      if (subCategoryName) {
        if (!acc[parentCategoryName].subCategories[subCategoryName]) {
          acc[parentCategoryName].subCategories[subCategoryName] = [];
        }
        acc[parentCategoryName].subCategories[subCategoryName].push(menu);
      } else {
        // 하위 카테고리가 없는 경우, 상위 카테고리에 메뉴 추가
        acc[parentCategoryName].menus.push(menu);
      }

      return acc;
    }, {});

  /**
   * 순서 드래그앤드랍 변경
   * --
   */
  const handleDrop = (updatedItems) => {
    const updatedOrderList = [...orderList]; // 기존 orderList 복사

    // updatedItems에 따라 orderList 업데이트
    updatedItems.forEach((item, index) => {
      const existingItemIndex = updatedOrderList.findIndex(
        (existing) => existing.menu_id === item.menu_id
      );

      if (existingItemIndex !== -1) {
        // 기존 항목이 있으면 order만 업데이트
        updatedOrderList[existingItemIndex].order = index + 1;
      } else {
        // 기존 항목이 없으면 새로 추가
        updatedOrderList.push({
          shop_menu_id: item.shop_menu_id,
          menu_id: item.menu_id,
          order: index + 1,
        });
      }
    });

    // 결과를 order 기준으로 정렬 (옵션)
    const sortedOrderList = updatedOrderList.sort((a, b) =>
      a.order > b.order ? 1 : -1
    );

    setOrderList(sortedOrderList); // orderList 업데이트
  };

  /**
   * 순서 변경
   * --
   */
  const handleUpdateOrder = async () => {
    const confirm = window.confirm(`변경한 순서를 저장하시겠습니까?`);

    if (confirm) {
      if (orderList?.length === 0) {
        MessageAlert.warning('순서를 수정하고 저장해주세요.');
        return;
      }
      try {
        setIsLoading(true);

        // map과 Promise.all로 요청 처리
        const responses = await Promise.all(
          orderList.map(async (item) => {
            const { shop_menu_id, menu_id, order } = item;
            const payload = { shop_menu_id, menu_id, order };

            try {
              const { status } = await updateShopMenu(payload);

              if (status !== 200) {
                throw new Error(
                  `Failed to update shop_menu_id: ${shop_menu_id}`
                );
              }
              return { success: true, shop_menu_id };
            } catch (error) {
              console.error(`[UPDATE SHOPMENU ITEM ERROR]:`, error);
              MessageAlert.error(`순서 변경 실패: ${shop_menu_id}`);
              return { success: false, shop_menu_id };
            }
          })
        );

        // 모든 요청이 성공했는지 확인
        const isSuccess = responses.every((res) => res.success);

        if (isSuccess) {
          MessageAlert.success('순서가 변경되었습니다.');
          setIsRefetch(true);
          const queryKey = getQueryKey['SHOPMENU']();
          queryClient.invalidateQueries({
            queryKey: queryKey.queryKey,
          });
        } else {
          MessageAlert.error('일부 순서 변경에 실패했습니다.');
        }
      } catch (err) {
        MessageAlert.error('순서 변경이 실패했습니다.');
        console.error('[UPDATE SHOPMENU ERROR]:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    // ShopMenuData와 newShopMenu 필터 적용
    const ShopMenuData = ShopMenus?.data?.filter(
      (shopmenu) =>
        shopmenu?.shop_information_id === userInfo?.shop_information_id
    );

    const shopMenuMap = new Map(
      ShopMenuData?.map((shopmenu) => [
        shopmenu.menu_id,
        {
          is_offline: shopmenu.is_offline,
          shop_menu_id: shopmenu.shop_menu_id,
          num: shopmenu.num || 0,
          order: shopmenu.order || Number.MAX_SAFE_INTEGER, // `order` 기본값 설정
        },
      ])
    );

    const newShopMenu = ShopMenuData?.filter(
      (shopmenu) => shopmenu?.is_show === 'Y'
    )?.map((item) => item.menu_id);

    const NewCategories = Categories?.data?.filter(
      (item) => item.application_status === 'APPROVE'
    );

    // categoryMap 생성
    const createCategoryMap = () =>
      (NewCategories || []).reduce((acc, category) => {
        if (!category.parents_id) {
          acc[category.product_category_id] = {
            ...category,
            subCategories: [],
            menus: [],
          };
        } else {
          const parentCategory = acc[category.parents_id];
          if (parentCategory) {
            parentCategory.subCategories.push({
              ...category,
              menus: [],
            });
          }
        }
        return acc;
      }, {});

    // newData를 위한 categoryMap (필터 적용)
    const newDataCategoryMap = createCategoryMap();

    // newData 생성 (필터 적용)
    (Menus?.data || [])
      .filter((menu) => newShopMenu?.includes(menu.menu_id))
      .forEach((menu) => {
        const category = newDataCategoryMap[menu?.product_category_id];
        const shopMenuInfo = shopMenuMap.get(menu.menu_id) || {};
        const isOffline = shopMenuInfo?.is_offline || false;
        const shopMenuId = shopMenuInfo?.shop_menu_id || null;
        const num = shopMenuInfo?.num ?? menu.num; // `ShopMenuData`의 `num` 값을 우선 사용
        const order = shopMenuInfo?.order; // `order` 값

        if (category) {
          category.menus.push({
            ...menu,
            is_offline: isOffline,
            shop_menu_id: shopMenuId,
            num,
            order, // `order` 값 추가
          });
        } else {
          // 하위 카테고리에서 메뉴 찾기
          const parentCategory = Object.values(newDataCategoryMap).find((c) =>
            c.subCategories?.some(
              (subCategory) =>
                subCategory?.product_category_id === menu?.product_category_id
            )
          );
          if (parentCategory) {
            const subCategory = parentCategory.subCategories?.find(
              (subCategory) =>
                subCategory?.product_category_id === menu?.product_category_id
            );
            if (subCategory) {
              subCategory.menus.push({
                ...menu,
                is_offline: isOffline,
                shop_menu_id: shopMenuId,
                num, // `num` 값 설정
                order, // `order` 값 추가
              }); // 하위 카테고리에 메뉴 추가
            }
          }
        }
      });

    // 메뉴 정렬
    Object.values(newDataCategoryMap).forEach((category) => {
      // 상위 카테고리 메뉴 정렬
      category.menus.sort((a, b) => a.order - b.order);

      // 하위 카테고리 메뉴 정렬
      category.subCategories?.forEach((subCategory) => {
        subCategory.menus.sort((a, b) => a.order - b.order);
      });
    });

    const newData =
      Object.keys(newDataCategoryMap).length > 0
        ? Object.values(newDataCategoryMap)?.sort(
            (a, b) => (a.order || 0) - (b.order || 0)
          )
        : [];

    // 사용하지 않는 카테고리 필터
    const filteredData = (newData ?? [])
      // 1) 각 카테고리의 subCategories를 '메뉴가 있는 것만' 남기고 재구성
      .map((category) => ({
        ...category,
        subCategories: (category.subCategories ?? []).filter(
          (sub) => sub.menus?.length
        ),
      }))
      // 2) 최종적으로 '상위 menus가 있거나, 재구성된 subCategories가 남아 있는' 것만 남기기
      .filter(
        ({ menus = [], subCategories = [] }) =>
          menus.length > 0 || subCategories.length > 0
      );

    setData(filteredData);

    // menuData를 위한 categoryMap (필터 없음)
    const menuDataCategoryMap = createCategoryMap();

    // menuData 생성 (필터 없음)
    (Menus?.data || []).forEach((menu) => {
      if (menu.application_status !== 'APPROVE' || menu.is_show !== 'Y') return;
      const category = menuDataCategoryMap[menu?.product_category_id];
      if (category) {
        category.menus.push(menu);
      } else {
        // 하위 카테고리에서 메뉴 찾기
        const parentCategory = Object.values(menuDataCategoryMap).find((c) =>
          c.subCategories?.some(
            (subCategory) =>
              subCategory?.product_category_id === menu?.product_category_id
          )
        );
        if (parentCategory) {
          const subCategory = parentCategory.subCategories?.find(
            (subCategory) =>
              subCategory?.product_category_id === menu?.product_category_id
          );
          if (subCategory) {
            subCategory.menus.push(menu); // 하위 카테고리에 메뉴 추가
          }
        }
      }
    });

    const menuData =
      Object.keys(menuDataCategoryMap).length > 0
        ? Object.values(menuDataCategoryMap)?.sort(
            (a, b) => (a.order || 0) - (b.order || 0)
          )
        : [];
    setMenuData(menuData);

    const selectMenuData = Menus?.data
      ?.filter((menu) => newShopMenu?.includes(menu.menu_id))
      .map((menu) => {
        // menu의 product_category_id를 사용해 해당 카테고리를 찾음
        const category = NewCategories?.find(
          (category) =>
            category?.product_category_id === menu?.product_category_id
        );

        // parents_id가 존재하는 경우, 상위 카테고리 이름을 찾음
        const parentCategory = category?.parents_id
          ? NewCategories?.find(
              (parentCat) =>
                parentCat.product_category_id === category.parents_id
            )
          : null;

        // category_name과 parents_category_name을 menu에 추가
        return {
          ...menu,
          product_category_order: category ? category.order : null,
          parents_category_name: parentCategory
            ? parentCategory.product_category_name
            : null,
          parents_id: category ? category?.parents_id : null,
          category_name: category ? category?.product_category_name : '기타',
        };
      });

    setPrivateShopMenu(ShopMenuData);
    setShopMenuData(newShopMenu);
    setSelectedMenus(selectMenuData);
    // eslint-disable-next-line
  }, [ShopMenus, Menus, Categories]);

  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && (
        <LoadingPage text={'데이터를 처리중입니다. 잠시만 기다려주세요.'} />
      )}
      <MenuManagementPresenter
        menuList={data}
        menuData={menuData}
        shopMenuData={shopMenuData}
        selectedMenus={selectedMenus}
        selectedItem={selectedItem}
        isEditMode={isEditMode}
        isOpened={isOpened}
        modalData={modalData}
        modalType={modalType}
        files={files}
        thumbnailFiles={thumbnailFiles}
        isRefetch={isRefetch}
        options={options}
        nutritionalInfo={nutritionalInfo}
        groupedSelectedMenus={groupedSelectedMenus}
        onSelectMenu={handleSelect}
        onSelect={setSelectedItem}
        onEditMode={handleEdit}
        onOpen={handleModal}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onFileChange={setFiles}
        onThumbnailFileChange={setThumbnailFiles}
        onOptionList={setOptions}
        onOptionChange={handleOption}
        onNuritionalChange={handleNutritionalInfo}
        onDrop={handleDrop}
        onUpdateOrder={handleUpdateOrder}
      />
    </>
  );
};

export default MenuManagementContainer;
